import { useState, useEffect, useCallback, useRef } from 'react'

const parseFiltersFromUrl = (searchParams) => {
  const filters = {}

  try {
    for (const [key, value] of searchParams.entries()) {
      // Skip empty or invalid keys/values
      if (!key || key === 'query' || key === 'page' || !value) {
        continue
      }

      if (key.endsWith('[]')) {
        const filterName = key.replace('[]', '')
        if (filterName) { // Make sure filterName is not empty
          if (!filters[filterName]) {
            filters[filterName] = []
          }
          // Only add if not already present to prevent duplicates
          if (!filters[filterName].includes(value)) {
            filters[filterName].push(value)
          }
        }
      } else {
        // Only handle non-query, non-page parameters as filters
        // Don't accidentally convert query to array
        filters[key] = [value]
      }
    }
  } catch (error) {
    console.warn('Error parsing URL filters:', error)
    return {}
  }

  return filters
}

const updateFiltersInUrl = (searchParams, filters) => {
  // Clear all existing filter parameters
  const keysToDelete = []
  for (const key of searchParams.keys()) {
    if (key !== 'query' && key !== 'page') {
      keysToDelete.push(key)
    }
  }

  // Delete all filter keys
  keysToDelete.forEach(key => searchParams.delete(key))

  // Add new filter parameters
  Object.entries(filters).forEach(([filterName, values]) => {
    if (values && values.length > 0) {
      values.forEach(value => {
        searchParams.append(`${filterName}[]`, value)
      })
    }
  })
}

const updateUrlWithPage = (page) => {
  let newPath = window.location.pathname

  // Remove existing /page/X/ from path
  newPath = newPath.replace(/\/page\/\d+\/?/, '')

  // Add new page number if not page 1
  if (page === '1' || page === 1) {
    // Ensure path ends with /
    if (!newPath.endsWith('/')) {
      newPath += '/'
    }
  } else {
    newPath = newPath.replace(/\/$/, '') + '/page/' + page + '/'
  }

  // Get current query params
  const url = new URL(window.location)
  const query = url.searchParams.get('query')
  const filters = parseFiltersFromUrl(url.searchParams)

  // Build final URL
  let finalUrl = newPath

  // Add query parameter if exists
  if (query) {
    finalUrl += `?query=${encodeURIComponent(query)}`
  }

  // Add filter parameters
  if (Object.keys(filters).length > 0) {
    let filterParams = []
    Object.entries(filters).forEach(([filterName, values]) => {
      if (values && values.length > 0) {
        values.forEach(value => {
          filterParams.push(`${filterName}[]=${encodeURIComponent(value)}`)
        })
      }
    })

    if (filterParams.length > 0) {
      const separator = finalUrl.includes('?') ? '&' : '?'
      finalUrl += separator + filterParams.join('&')
    }
  }

  // Update the URL
  window.history.replaceState({}, '', finalUrl)
}

export const useUrlState = () => {
  const urlUpdateTimeoutRef = useRef(null)

  const [urlState, setUrlState] = useState(() => {
    if (typeof window === 'undefined') return {}

    try {
      const url = new URL(window.location)

      // Parse page from path (/page/2/) or query param (?page=2)
      let page = '1'
      const pageMatch = url.pathname.match(/\/page\/(\d+)\/?/)
      if (pageMatch) {
        page = pageMatch[1]
      } else {
        page = url.searchParams.get('page') || '1'
      }

      return {
        query: url.searchParams.get('query') || '',
        page: page,
        filters: parseFiltersFromUrl(url.searchParams)
      }
    } catch (error) {
      console.warn('Error initializing URL state:', error)
      return {
        query: '',
        page: '1',
        filters: {}
      }
    }
  })

  const updateUrlState = useCallback((newState) => {
    setUrlState(prev => {
      const updated = { ...prev, ...newState }

      if (typeof window !== 'undefined') {
        // Handle page updates with WordPress-style URLs
        if (newState.page !== undefined) {
          updateUrlWithPage(newState.page)
        } else {
          // Clear any existing timeout for search updates
          if (urlUpdateTimeoutRef.current) {
            clearTimeout(urlUpdateTimeoutRef.current)
          }

          // For query updates, debounce the URL update
          if (newState.query !== undefined) {
            urlUpdateTimeoutRef.current = setTimeout(() => {
              try {
                const url = new URL(window.location)

                if (updated.query && updated.query.length > 0) {
                  url.searchParams.set('query', updated.query)
                } else {
                  url.searchParams.delete('query')
                }

                // Also handle any current filters
                updateFiltersInUrl(url.searchParams, updated.filters || {})

                // Update the URL with the new state
                window.history.replaceState({}, '', url.toString())
              } catch (error) {
                console.warn('Error updating URL for query:', error)
              }
            }, 2500) // 2.5s delay for URL updates
          } else if (newState.filters !== undefined) {
            // Handle filter updates immediately
            try {
              const url = new URL(window.location)

              // Handle filter updates
              updateFiltersInUrl(url.searchParams, updated.filters)

              // Update the URL with the new state
              window.history.replaceState({}, '', url.toString())
            } catch (error) {
              console.warn('Error updating URL for filters:', error)
            }
          }
        }
      }

      return updated
    })
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (urlUpdateTimeoutRef.current) {
        clearTimeout(urlUpdateTimeoutRef.current)
      }
    }
  }, [])

  const updateQueryImmediate = useCallback((query) => {
    // Clear any pending timeout for query updates
    if (urlUpdateTimeoutRef.current) {
      clearTimeout(urlUpdateTimeoutRef.current)
      urlUpdateTimeoutRef.current = null
    }

    // Update state
    setUrlState(prev => {
      const updated = { ...prev, query }

      if (typeof window !== 'undefined') {
        try {
          const url = new URL(window.location)

          if (query && query.length > 0) {
            url.searchParams.set('query', query)
          } else {
            url.searchParams.delete('query')
          }

          // Also handle any current filters
          updateFiltersInUrl(url.searchParams, updated.filters || {})

          // Update the URL immediately
          window.history.replaceState({}, '', url.toString())
        } catch (error) {
          console.warn('Error updating URL for immediate query:', error)
        }
      }

      return updated
    })
  }, [])

  return {
    query: urlState.query,
    page: urlState.page,
    filters: urlState.filters,
    updateQuery: (query) => updateUrlState({ query }),
    updateQueryImmediate,
    updatePage: (page) => updateUrlState({ page: page.toString() }),
    updateFilters: (filters) => updateUrlState({ filters })
  }
}
